import { useEffect, useMemo, useState } from "react";
import { Cpu, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";

const JOBS = [
  ["writing", "text", "Writing & editing", "New titles, conversions, rewrites, listings, captions"],
  ["fact_check", "text", "Fact checking (with web search)", "Checks claims and builds the Sources page"],
  ["drawing", "image", "Drawings", "Covers, vignettes, chapter art, Instagram images"],
];

const price = (m, kind) => {
  if (!m) return "";
  if (kind === "image") return +m.image ? `~US$${(+m.image).toFixed(3)} / image` : "Charged per image by OpenRouter (GPT Image 2 ≈ US$0.05 each)";
  const p = +m.prompt * 1e6, c = +m.completion * 1e6;
  return p || c ? `US$${p.toFixed(2)} in / US$${c.toFixed(2)} out per 1M tokens` : "";
};

export const ModelPicker = ({ list, value, onChange, kind, id }) => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const t = q.toLowerCase();
    const hits = list.filter((m) => !t || m.id.toLowerCase().includes(t) || m.name.toLowerCase().includes(t));
    return hits.slice(0, 200);
  }, [list, q]);
  const cur = list.find((m) => m.id === value);
  return (
    <div className="space-y-1.5">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search models, e.g. claude, gemini, gpt…" className="rounded-none bg-white h-8 text-xs" data-testid={`ai-model-search-${id}`} />
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 w-full border border-input bg-white px-2 text-sm" data-testid={`ai-model-select-${id}`}>
        {!shown.some((m) => m.id === value) && <option value={value}>{cur?.name || value}</option>}
        {shown.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.id}</option>)}
      </select>
      <div className="text-[11px] text-[#5C5255]" data-testid={`ai-model-price-${id}`}>{price(cur, kind)}</div>
    </div>
  );
};

export const AIModelsCard = () => {
  const [s, setS] = useState(null);
  const [models, setModels] = useState({ text: [], image: [] });
  const [sel, setSel] = useState({});
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/ai-settings").then((r) => { setS(r.data); setSel(r.data.models); }).catch((e) => setErr(errMsg(e)));
    api.get("/ai-settings/models").then((r) => setModels(r.data)).catch(() => {});
  }, []);
  if (err) return <div className="border border-[#9B2C2C]/30 bg-[#FBEDED] p-5 text-sm text-[#9B2C2C]" data-testid="ai-settings-error">Couldn't load AI settings: {err}</div>;
  if (!s) return null;

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/ai-settings", { ...sel, ...(key ? { api_key: key } : {}) });
      setS(r.data);
      setSel(r.data.models);
      setKey("");
      toast.success("AI settings saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const cr = s.credit;
  return (
    <div className="bg-[#FFFDF9] border border-[#2A2325]/10 p-7 space-y-6" data-testid="ai-models-card">
      <div className="flex items-start gap-4">
        <Cpu size={24} className="text-[#5E2B38] shrink-0" />
        <div>
          <div className="eyebrow">AI models · OpenRouter</div>
          <p className="text-sm text-[#5C5255] mt-1">All AI work runs on your OpenRouter account. Pick the model for each job below.</p>
          {cr && (
            <p className="text-sm mt-2" data-testid="ai-credit">
              Credit left: <strong>US${(+cr.remaining).toFixed(2)}</strong>{cr.limit ? ` of US$${cr.limit} limit` : ""}
            </p>
          )}
        </div>
      </div>
      <label className="block text-sm">OpenRouter API key
        <Input type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={s.has_key ? `Saved (${s.key_hint}) — paste a new key to change` : "sk-or-v1-…"} className="mt-1.5 rounded-none bg-white" data-testid="ai-key-input" />
      </label>
      <div className="grid md:grid-cols-3 gap-6">
        {JOBS.map(([job, kind, label, help]) => (
          <div key={job}>
            <div className="text-sm font-medium">{label}</div>
            <div className="text-xs text-[#5C5255] mb-2">{help}</div>
            <ModelPicker list={models[kind]} value={sel[job] || ""} onChange={(v) => setSel({ ...sel, [job]: v })} kind={kind} id={job} />
          </div>
        ))}
      </div>
      <p className="text-xs text-[#5C5255]">Fact checking uses OpenRouter web search, which adds a small fee per check. Drawings need an image model.</p>
      <Button onClick={save} disabled={busy} className="rounded-none gap-2 bg-[#5E2B38]" data-testid="ai-settings-save-btn">
        {busy && <Loader2 size={14} className="animate-spin" />} Save AI settings
      </Button>
    </div>
  );
};
